import React, { useState, useEffect, useRef } from 'react';
import { watchlistAPI } from '../services/api';
import './Watchlist.css';

function Watchlist() {
  const [watchlist, setWatchlist] = useState([]);
  const [newSymbol, setNewSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validating, setValidating] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    fetchWatchlist();
    
    // Close suggestions when clicking outside
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      const response = await watchlistAPI.list();
      setWatchlist(response.data);
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    if (!newSymbol.trim()) return;

    try {
      setError(null);
      setValidating(true);
      await watchlistAPI.add(newSymbol.toUpperCase());
      setNewSymbol('');
      setSuggestions([]);
      setShowSuggestions(false);
      fetchWatchlist();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add stock');
    } finally {
      setValidating(false);
    }
  };

  const handleInputChange = async (value) => {
    setNewSymbol(value.toUpperCase());
    
    if (value.length >= 1) {
      try {
        const response = await watchlistAPI.search(value.toUpperCase());
        setSuggestions(response.data);
        setShowSuggestions(response.data.length > 0);
      } catch (error) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (symbol) => {
    setNewSymbol(symbol);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleRemoveStock = async (id) => {
    try {
      await watchlistAPI.remove(id);
      fetchWatchlist();
    } catch (error) {
      console.error('Failed to remove stock:', error);
    }
  };

  return (
    <div className="watchlist-widget">
      <h3>Watchlist</h3>
      
      <form onSubmit={handleAddStock} className="add-stock-form">
        <div className="autocomplete-wrapper" ref={wrapperRef}>
          <input
            type="text"
            placeholder="Add stock symbol (e.g., AAPL)"
            value={newSymbol}
            onChange={(e) => handleInputChange(e.target.value)}
            className="stock-input"
            maxLength={5}
            disabled={validating}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSelectSuggestion(suggestion.symbol)}
                >
                  <span className="suggestion-symbol">{suggestion.symbol}</span>
                  <span className="suggestion-name">{suggestion.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button type="submit" className="add-btn" disabled={validating}>
          {validating ? '...' : 'Add'}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : watchlist.length === 0 ? (
        <div className="empty-state">
          <p>No stocks in watchlist</p>
          <p className="hint">Add stocks to automatically scan for signals</p>
        </div>
      ) : (
        <div className="watchlist-items">
          {watchlist.map((stock) => (
            <div key={stock.id} className="watchlist-item">
              <span className="symbol">{stock.symbol}</span>
              <button 
                onClick={() => handleRemoveStock(stock.id)}
                className="remove-btn"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="scan-info">
        <small>🔄 Auto-scans every 5 minutes</small>
      </div>
    </div>
  );
}

export default Watchlist;
